import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, Row, Col, Collapse, Alert, Spinner } from 'react-bootstrap';
import { parentService } from '../../services/api';
import SendBroadcastEmailModal from './SendBroadcastEmailModal';
import SearchParentUserModal from './SearchParentUserModal';
import EmailWarningModal from '../Common/EmailWarningModal';
import { checkEmailsForWarnings } from '../../utils/emailValidation';

const ParentSection = ({ userId, onParentChange, isRegistration = false, parentData: propParentData = null, headerBg = null, defaultOpen = false, allowUserSearch = !isRegistration }) => {
  const [showParentSection, setShowParentSection] = useState(isRegistration || defaultOpen);
  const [parentData, setParentData] = useState(propParentData || null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [infoNotice, setInfoNotice] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [emailWarnings, setEmailWarnings] = useState([]);
  const [showEmailWarningModal, setShowEmailWarningModal] = useState(false);
  const [formData, setFormData] = useState({
    firstName: propParentData?.firstName || '',
    lastName: propParentData?.lastName || '',
    email: propParentData?.email || '',
    phoneNumber: propParentData?.phoneNumber || '',
    fiscalCode: propParentData?.fiscalCode || '',
    dateOfBirth: propParentData?.dateOfBirth ? propParentData.dateOfBirth.split('T')[0] : '',
    birthMunicipality: propParentData?.birthMunicipality || '',
    residenceMunicipality: propParentData?.residenceMunicipality || '',
    residenceProvince: propParentData?.residenceProvince || '',
    residencePostalCode: propParentData?.residencePostalCode || '',
    residenceStreet: propParentData?.residenceStreet || ''
  });

  const isSavedParent = Boolean(parentData?.id);

  const loadParentData = useCallback(async () => {
    if (!userId || !showParentSection || parentData || propParentData) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await parentService.getParent(userId);
      if (response.success && response.data) {
        setParentData(response.data);
        setFormData({
          firstName: response.data.firstName || '',
          lastName: response.data.lastName || '',
          email: response.data.email || '',
          phoneNumber: response.data.phoneNumber || '',
          fiscalCode: response.data.fiscalCode || '',
          dateOfBirth: response.data.dateOfBirth ? response.data.dateOfBirth.split('T')[0] : '',
          birthMunicipality: response.data.birthMunicipality || '',
          residenceMunicipality: response.data.residenceMunicipality || '',
          residenceProvince: response.data.residenceProvince || '',
          residencePostalCode: response.data.residencePostalCode || '',
          residenceStreet: response.data.residenceStreet || ''
        });
        // Notify parent component about loaded data
        onParentChange?.(response.data);
      }
    } catch (err) {
      setError('Errore nel caricamento dati genitore');
      console.error('Error loading parent data:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, showParentSection, parentData, propParentData, onParentChange]);

  useEffect(() => {
    if (showParentSection && userId && !parentData && !propParentData && !isRegistration) {
      loadParentData();
    }
  }, [showParentSection, userId, parentData, propParentData, loadParentData, isRegistration]);

  useEffect(() => {
    if (defaultOpen) {
      setShowParentSection(true);
    }
  }, [defaultOpen]);

  useEffect(() => {
    if (propParentData) {
      if (propParentData.id && propParentData.id !== parentData?.id) {
        setParentData(propParentData);
        setFormData({
          firstName: propParentData.firstName || '',
          lastName: propParentData.lastName || '',
          email: propParentData.email || '',
          phoneNumber: propParentData.phoneNumber || '',
          fiscalCode: propParentData.fiscalCode || '',
          dateOfBirth: propParentData.dateOfBirth ? propParentData.dateOfBirth.split('T')[0] : '',
          birthMunicipality: propParentData.birthMunicipality || '',
          residenceMunicipality: propParentData.residenceMunicipality || '',
          residenceProvince: propParentData.residenceProvince || '',
          residencePostalCode: propParentData.residencePostalCode || '',
          residenceStreet: propParentData.residenceStreet || ''
        });
      } else if (!parentData?.id && !propParentData.id) {
        setFormData(prev => ({
          ...prev,
          firstName: propParentData.firstName || prev.firstName,
          lastName: propParentData.lastName || prev.lastName,
          email: propParentData.email || prev.email,
          phoneNumber: propParentData.phoneNumber || prev.phoneNumber,
          fiscalCode: propParentData.fiscalCode || prev.fiscalCode,
          dateOfBirth: propParentData.dateOfBirth ? propParentData.dateOfBirth.split('T')[0] : prev.dateOfBirth,
          birthMunicipality: propParentData.birthMunicipality || prev.birthMunicipality,
          residenceMunicipality: propParentData.residenceMunicipality || prev.residenceMunicipality,
          residenceProvince: propParentData.residenceProvince || prev.residenceProvince,
          residencePostalCode: propParentData.residencePostalCode || prev.residencePostalCode,
          residenceStreet: propParentData.residenceStreet || prev.residenceStreet
        }));
      }
    }
  }, [propParentData, parentData?.id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    let processedValue = value;
    if (name === 'residenceProvince') {
      processedValue = value.toUpperCase().slice(0, 2);
    } else if (name === 'residencePostalCode') {
      processedValue = value.replace(/\D/g, '').slice(0, 5);
    } else if (name === 'fiscalCode') {
      processedValue = value.toUpperCase().slice(0, 16);
    }
    
    const updatedFormData = { ...formData, [name]: processedValue };
    setFormData(updatedFormData);
    
    // Notify parent component about changes
    onParentChange?.(updatedFormData);
  };

  const handleSaveParent = async (bypassEmailWarning = false) => {
    if (isRegistration) {
      // In registration mode, just notify parent component
      onParentChange?.(formData);
      return;
    }
    
    if (!userId) return;

    // In UserForm mode, all fields are optional for admin; check only that at least one field has data
    if (!hasParentData) {
      setError('Inserisci almeno un dato del genitore per procedere al salvataggio.');
      return;
    }

    if (!bypassEmailWarning) {
      const warnings = checkEmailsForWarnings([
        { label: 'Email Genitore', value: formData.email, fieldId: 'parent-email' }
      ]);
      if (warnings.length > 0) {
        setEmailWarnings(warnings);
        setShowEmailWarningModal(true);
        return;
      }
    }
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      let response;
      if (isSavedParent) {
        // Update existing parent
        response = await parentService.updateParent(userId, formData);
      } else {
        // Create new parent
        response = await parentService.createParent({ ...formData, userId });
      }
      
      if (response.success) {
        setParentData(response.data);
        setSuccess(response.message || 'Dati genitore salvati con successo');
        onParentChange?.(response.data);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.message || 'Errore nel salvataggio dei dati del genitore');
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error saving parent:', err);
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = () => {
    setShowParentSection(!showParentSection);
  };

  const handleSelectParentUser = (user) => {
    const newFormData = {
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || user.loginEmail || '',
      phoneNumber: user.phoneNumber || '',
      fiscalCode: user.fiscalCode ? user.fiscalCode.toUpperCase().slice(0, 16) : '',
      dateOfBirth: user.dateOfBirth ? user.dateOfBirth.split('T')[0] : '',
      birthMunicipality: user.birthMunicipality || '',
      residenceMunicipality: user.residenceMunicipality || '',
      residenceProvince: user.residenceProvince ? user.residenceProvince.toUpperCase().slice(0, 2) : '',
      residencePostalCode: user.residencePostalCode ? user.residencePostalCode.replace(/\D/g, '').slice(0, 5) : '',
      residenceStreet: user.address || ''
    };

    setFormData(newFormData);
    onParentChange?.(newFormData);
    setInfoNotice(`Dati del genitore compilati da ${user.firstName} ${user.lastName}. Ricordati di salvare le modifiche.`);
  };

  // Check if form has data
  const hasParentData = Object.values(formData).some(value => value && value.trim() !== '');

  const useBlueHeader = headerBg === 'primary' || (headerBg === null && isRegistration);

  return (
    <Card className={useBlueHeader ? "mb-4 shadow-sm border-0" : "mt-3"}>
      <Card.Header 
        className={`d-flex justify-content-between align-items-center ${
          useBlueHeader 
            ? 'bg-primary text-white py-3 border-bottom' 
            : (!isRegistration ? 'cursor-pointer' : '')
        }`}
        onClick={!isRegistration ? toggleSection : undefined}
        style={!isRegistration ? { cursor: 'pointer' } : {}}
      >
        {useBlueHeader ? (
          <h5 className="mb-0 text-white">
            <i className="fas fa-user-friends me-2"></i>
            Dati Genitore
            {isSavedParent && (
              <span className="badge bg-success ms-2" data-testid="parent-saved-badge">
                <i className="fas fa-check"></i>
              </span>
            )}
          </h5>
        ) : (
          <h6 className="mb-0">
            <i className="fas fa-user-friends me-2"></i>
            Dati Genitore
            {isSavedParent && (
              <span className="badge bg-success ms-2" data-testid="parent-saved-badge">
                <i className="fas fa-check"></i>
              </span>
            )}
          </h6>
        )}
        {!isRegistration && (
          <i className={`fas fa-chevron-${showParentSection ? 'up' : 'down'} ${useBlueHeader ? 'text-white' : ''}`}></i>
        )}
      </Card.Header>
      
      <Collapse in={isRegistration || showParentSection}>
        <Card.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError('')} dismissible>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert variant="success" onClose={() => setSuccess('')} dismissible>
              {success}
            </Alert>
          )}
          
          {loading ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
              <span className="ms-2">Caricamento dati genitore...</span>
            </div>
          ) : (
            <>
              {parentData?.parentUser && (
                <Alert variant="primary" className="d-flex align-items-center mb-3 py-2 px-3" data-testid="linked-parent-alert">
                  <i className="fas fa-link text-primary fs-5 me-2"></i>
                  <div>
                    <span className="small text-muted">Account Sportus collegato: </span>
                    <strong className="text-primary">
                      {parentData.parentUser.firstName} {parentData.parentUser.lastName}
                    </strong>
                    {(parentData.parentUser.loginEmail || parentData.parentUser.email) && (
                      <span className="text-muted ms-2 small">
                        ({parentData.parentUser.loginEmail || parentData.parentUser.email})
                      </span>
                    )}
                  </div>
                </Alert>
              )}

              {!isRegistration && allowUserSearch && (
                <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2 pb-2 border-bottom">
                  <div className="text-muted small">
                    <i className="fas fa-info-circle me-1 text-primary"></i>
                    Compila velocemente i campi recuperando i dati di un utente maggiorenne registrato.
                  </div>
                  <Button
                    type="button"
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowSearchModal(true)}
                    data-testid="search-parent-user-btn"
                  >
                    <i className="fas fa-search me-1"></i>
                    Cerca da utente registrato
                  </Button>
                </div>
              )}

              {infoNotice && (
                <Alert variant="info" onClose={() => setInfoNotice('')} dismissible data-testid="parent-prefilled-notice">
                  <i className="fas fa-info-circle me-2"></i>
                  {infoNotice}
                </Alert>
              )}

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nome *</Form.Label>
                    <Form.Control
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="Nome genitore"
                      required={isRegistration}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cognome *</Form.Label>
                    <Form.Control
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Cognome genitore"
                      required={isRegistration}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="parent-email">
                    <Form.Label>Email *</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="email@genitore.com"
                      required={isRegistration}
                    />
                    {!isRegistration && formData.email && (
                      <button
                        type="button"
                        className="btn btn-link p-0 mt-1 text-primary small d-inline-flex align-items-center"
                        onClick={() => setShowEmailModal(true)}
                      >
                        <i className="fas fa-envelope me-1"></i>
                        Scrivi email
                      </button>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Telefono *</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      placeholder="+39 339 1234567"
                      required={isRegistration}
                    />
                    {formData.phoneNumber && (
                      <a
                        href={`https://wa.me/${formData.phoneNumber.replace(/[^\d+]/g, '').replace(/^\+/, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="d-inline-flex align-items-center mt-1 text-success small"
                      >
                        <i className="fab fa-whatsapp me-1"></i>
                        Scrivi Whatsapp
                      </a>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Codice Fiscale *</Form.Label>
                    <Form.Control
                      type="text"
                      name="fiscalCode"
                      value={formData.fiscalCode}
                      onChange={handleInputChange}
                      placeholder="RSSMRA70A01H501X"
                      maxLength={16}
                      required={isRegistration}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Data di Nascita *</Form.Label>
                    <Form.Control
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      required={isRegistration}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Luogo di Nascita *</Form.Label>
                    <Form.Control
                      type="text"
                      name="birthMunicipality"
                      value={formData.birthMunicipality}
                      onChange={handleInputChange}
                      placeholder="Milano"
                      required={isRegistration}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Indirizzo *</Form.Label>
                    <Form.Control
                      type="text"
                      name="residenceStreet"
                      value={formData.residenceStreet}
                      onChange={handleInputChange}
                      placeholder="Via Roma 123"
                      required={isRegistration}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Comune *</Form.Label>
                    <Form.Control
                      type="text"
                      name="residenceMunicipality"
                      value={formData.residenceMunicipality}
                      onChange={handleInputChange}
                      placeholder="Roma"
                      required={isRegistration}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Provincia *</Form.Label>
                    <Form.Control
                      type="text"
                      name="residenceProvince"
                      value={formData.residenceProvince}
                      onChange={handleInputChange}
                      placeholder="RM"
                      maxLength={2}
                      required={isRegistration}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>CAP *</Form.Label>
                    <Form.Control
                      type="text"
                      name="residencePostalCode"
                      value={formData.residencePostalCode}
                      onChange={handleInputChange}
                      placeholder="00100"
                      maxLength={5}
                      required={isRegistration}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Save Parent Button - Only show if not in registration mode and there's data to save */}
              {!isRegistration && hasParentData && (
                <div className="text-end mt-3">
                  <Button 
                    type="button"
                    variant="primary" 
                    onClick={handleSaveParent}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Salvataggio...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        {isSavedParent ? 'Aggiorna Dati Genitore' : 'Salva Dati Genitore'}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Collapse>

      {/* Email Modal genitore */}
      <SendBroadcastEmailModal
        show={showEmailModal}
        onHide={() => setShowEmailModal(false)}
        selectedUserIds={[]}
        selectedUsers={[]}
        emailAddresses={formData.email ? [formData.email] : []}
        onSuccess={(msg) => { setSuccess(msg); setShowEmailModal(false); }}
        onError={(msg) => setError(msg)}
      />

      {/* Modal ricerca utente per dati genitore */}
      <SearchParentUserModal
        show={showSearchModal}
        onHide={() => setShowSearchModal(false)}
        onSelectUser={handleSelectParentUser}
        currentUserId={userId}
      />

      {/* Modal avviso verifica email */}
      <EmailWarningModal
        show={showEmailWarningModal}
        warnings={emailWarnings}
        onCorrect={() => setShowEmailWarningModal(false)}
        onIgnoreAndProceed={() => {
          setShowEmailWarningModal(false);
          handleSaveParent(true);
        }}
      />
    </Card>
  );
};

export default ParentSection;
